import {
  Search,
  Bot,
  ShieldCheck,
  Banknote,
  Truck,
  ArrowRight,
} from "lucide-react";

const steps = [
  {
    icon: Search,
    num: "01",
    title: "Tell us what you need",
    desc: "Share product specs, quantities & destination.",
  },
  {
    icon: Bot,
    num: "02",
    title: "AI discovers suppliers",
    desc: "Models surface best suppliers with proven track records.",
  },
  {
    icon: ShieldCheck,
    num: "03",
    title: "Quality control & audit",
    desc: "Third-party QA inspects goods.",
  },
  {
    icon: Banknote,
    num: "04",
    title: "Safe payment mechanism",
    desc: "Seller gets paid after QA inspection & buyer pays net 15 to 60 days",
  },
  {
    icon: Truck,
    num: "05",
    title: "We ship & you track",
    desc: "Full logistics, real-time tracking, one all-in quote.",
  },
];

export default function HowItWorksSection() {
  return (
    <section
      id="how"
      className="py-10 md:py-16 bg-secondary/30 relative overflow-hidden"
    >
      <div className="container px-4 relative">
        <div className="text-center mb-16">
          <p className="text-sm font-semibold text-primary mb-3 tracking-wide uppercase">
            HOW IT WORKS
          </p>
          <h2 className="text-3xl md:text-[2.75rem] font-display font-bold text-foreground leading-tight tracking-tight">
            Five steps. <span className="text-gradient">Zero guesswork.</span>
          </h2>
          <p className="mt-4 text-lg md:text-xl text-muted-foreground max-w-xl mx-auto leading-snug">
            Our AI is trained on historic trade data - pricing, supplier
            reliability & logistics patterns, so every recommendation is backed
            by real-world insight.
          </p>
        </div>

        {/* Horizontal stepper on desktop, vertical on mobile */}
        <div className="hidden md:flex items-start justify-between relative mx-auto">
          {/* Connecting line */}
          <div className="absolute top-9 left-[10%] right-[10%] h-px bg-border z-0" />

          {steps.map((step) => (
            <div
              key={step.num}
              className="relative z-10 flex flex-col items-center text-center w-1/5 px-4 group"
            >
              <div className="w-[4.5rem] h-[4.5rem] rounded-2xl bg-card border border-border shadow-sm flex items-center justify-center mb-5 group-hover:border-primary/40 group-hover:shadow-md transition-all duration-300">
                <step.icon className="w-7 h-7 text-primary" />
              </div>
              <span className="text-sm font-semibold text-primary mb-2">
                Step {step.num}
              </span>
              <h3 className="text-base font-display font-bold text-foreground mb-2 leading-snug">
                {step.title}
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {step.desc}
              </p>
            </div>
          ))}
        </div>

        {/* Mobile: simple stacked cards */}
        <div className="md:hidden space-y-4">
          {steps.map((step) => (
            <div
              key={step.num}
              className="flex items-start gap-4 bg-card border border-border rounded-xl p-4"
            >
              <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <step.icon className="w-5 h-5 text-primary" />
              </div>
              <div>
                <span className="text-xs font-semibold text-primary">
                  Step {step.num}
                </span>
                <h3 className="text-sm font-display font-bold text-foreground mt-0.5">
                  {step.title}
                </h3>
                <p className="text-xs text-muted-foreground leading-relaxed mt-1">
                  {step.desc}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <a
            href="#contact"
            className="inline-flex items-center gap-2 px-7 py-3 text-sm font-bold rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            Procure Now <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
}
